import { safeMoneyFromCents } from '@libs/services/money';
import { Badge } from '@/components/ui/badge';
import { ADDED_AFTER_BOOKING_TEXT } from './addedAfterBooking';
import { LiveAddOn } from 'components/orders/Details/orderAddOnProvenance';

interface AdditionalFeesCardProps {
  /**
   * Booked items the order still carries - removed ones are reconciled out upstream. Priced as
   * the order records them now, not as the cart booked them: an admin can reprice a booked item
   * without unbooking it, and billing charges the live figure. Printing the booking-time
   * snapshot instead would leave these rows short of the Subtotal they are meant to explain.
   */
  addOns?: LiveAddOn[];
  newAddOns?: LiveAddOn[];
  cityFeeInCents?: number;
  cityName?: string;
}

/**
 * One priced line of the card. The grid keeps the amount in its own auto column so a long
 * title wraps instead of pushing the price out of view.
 */
const FeeRow = ({ title, price }: { title?: string; price: string }) => (
  <div
    data-testid="fee-row"
    className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-3 text-sm"
  >
    <p className="min-w-0 break-words">{title}</p>
    <p className="shrink-0 tabular-nums">{price}</p>
  </div>
);

export const AdditionalFeesCard = ({
  addOns,
  newAddOns,
  cityFeeInCents,
  cityName,
}: AdditionalFeesCardProps) => {
  // Don't render if there are no fees
  const hasAddOns = addOns && addOns.length > 0;
  const hasNewAddOns = newAddOns && newAddOns.length > 0;
  const hasCityFee = cityFeeInCents && cityFeeInCents > 0;

  if (!hasAddOns && !hasNewAddOns && !hasCityFee) {
    return null;
  }

  const hasAddOnBlock = !!hasAddOns || !!hasNewAddOns;

  return (
    <div className="card flex flex-col items-stretch gap-3 justify-start w-full p-4 mt-2">
      <div className="flex flex-row justify-between w-full text-sm">
        <p className="label">Additional Fees</p>
        <p className="label">Price</p>
      </div>

      {/* Add-ons and products of either provenance share one tinted block, so the category
          reads as a single group without a card inside a card. Non-add-on fees stay out of
          the tint. */}
      {hasAddOnBlock && (
        <div data-testid="add-on-block" className="w-full rounded-md bg-slate-50 p-3">
          {/* Add-ons / products booked with the original order */}
          {!!hasAddOns && (
            <div
              data-testid="existing-add-on-group"
              className="flex w-full flex-col gap-1.5"
            >
              {/* The global `.card` rule centres auto-width children, so the subgroup claims
                  the full width to keep its label on the same left edge as its rows. */}
              <p className="label text-xs">Existing Add-ons / Products</p>
              {addOns.map((addOn, ind) => (
                <FeeRow
                  key={`booked-${addOn._id}-${ind}`}
                  title={addOn.title}
                  price={safeMoneyFromCents(addOn.priceInCents || 0).toFormat()}
                />
              ))}
            </div>
          )}

          {/* Only meaningful when there are two subgroups to tell apart. */}
          {!!hasAddOns && !!hasNewAddOns && (
            <div
              data-testid="add-on-group-divider"
              aria-hidden="true"
              className="my-3 h-px w-full bg-slate-200"
            />
          )}

          {/* Add-ons / products added after booking - these move the order total, so they are
              labelled with the same amber attention treatment used elsewhere on the page. */}
          {!!hasNewAddOns && (
            <div data-testid="new-add-on-group" className="flex w-full flex-col gap-1.5">
              <div className="flex w-full flex-wrap items-center justify-start gap-x-2 gap-y-1">
                <p className={`label text-xs ${ADDED_AFTER_BOOKING_TEXT}`}>
                  New Add-ons / Products
                </p>
                {/* Provenance stated in words right where the admin reads the line items, so the
                    group is not distinguished by its amber label alone. */}
                <Badge variant="informational">Added after booking</Badge>
              </div>
              {newAddOns.map((addOn, ind) => (
                <FeeRow
                  key={`new-${addOn._id}-${ind}`}
                  title={addOn.title}
                  price={safeMoneyFromCents(addOn.priceInCents || 0).toFormat()}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* City Fee - not an add-on, so it stays outside both provenance groups and outside the
          tinted block. The rule above it only earns its place when a block precedes it. */}
      {!!hasCityFee && (
        <div
          data-testid="non-add-on-fees"
          className={`flex flex-col items-start self-start w-full${
            hasAddOnBlock ? ' border-t border-slate-200 pt-3' : ''
          }`}
        >
          <FeeRow title="City Fee" price={safeMoneyFromCents(cityFeeInCents).toFormat()} />
          <p className="mt-1 text-xs text-text-light">
            The city of {cityName || 'the delivery location'} charges a fee for waste disposal.
          </p>
        </div>
      )}
    </div>
  );
};
